/**
 * Persist payment proof File/Blob bytes so recorded payment attachments
 * stay viewable after refresh (names alone are not enough to open files).
 */

const DB_NAME = 'demandjet.paymentProofs';
const DB_VERSION = 1;
const STORE_NAME = 'proofs';

/** In-memory fallback when IndexedDB is unavailable (tests / private mode). */
const memoryStore = new Map();

export function paymentProofRecordId(matterId, paymentId, fileName) {
  return [
    String(matterId || '').trim(),
    String(paymentId || '').trim(),
    String(fileName || '').trim(),
  ].join('::');
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined' || !indexedDB) {
      resolve(null);
      return;
    }
    let request;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reject(err);
      return;
    }
    request.onerror = () => reject(request.error || new Error('IndexedDB open failed'));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('byPayment', ['matterId', 'paymentId'], { unique: false });
      }
    };
  });
}

async function putRecord(record) {
  memoryStore.set(record.id, record);
  try {
    const db = await openDb();
    if (!db) return;
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE_NAME).put(record);
    });
  } catch {
    // Memory copy already written for this session.
  }
}

async function getRecord(id) {
  if (memoryStore.has(id)) return memoryStore.get(id);
  try {
    const db = await openDb();
    if (!db) return null;
    const record = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    if (record) memoryStore.set(id, record);
    return record;
  } catch {
    return null;
  }
}

/**
 * Store proof files for one payment entry.
 * @returns {Promise<string[]>} saved file names
 */
export async function putPaymentProofFiles(matterId, paymentId, files) {
  const mid = String(matterId || '').trim();
  const pid = String(paymentId || '').trim();
  if (!mid || !pid) return [];
  const saved = [];
  for (const file of Array.from(files || [])) {
    if (!file) continue;
    const name = String(file.name || '').trim();
    if (!name) continue;
    await putRecord({
      id: paymentProofRecordId(mid, pid, name),
      matterId: mid,
      paymentId: pid,
      name,
      type: file.type || 'application/octet-stream',
      size: Number(file.size) || 0,
      blob: file,
      createdAt: new Date().toISOString(),
    });
    saved.push(name);
  }
  return saved;
}

export async function getPaymentProofRecord(matterId, paymentId, fileName) {
  return getRecord(paymentProofRecordId(matterId, paymentId, fileName));
}

/** Create an object URL for a stored proof, or null when missing. */
export async function resolvePaymentProofUrl(matterId, paymentId, fileName) {
  const record = await getPaymentProofRecord(matterId, paymentId, fileName);
  if (!record?.blob) return null;
  try {
    return URL.createObjectURL(record.blob);
  } catch {
    return null;
  }
}

async function deleteRecord(id) {
  memoryStore.delete(id);
  try {
    const db = await openDb();
    if (!db) return;
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE_NAME).delete(id);
    });
  } catch {
    // Memory copy already cleared for this session.
  }
}

/** Remove all stored proof blobs for one payment entry. */
export async function deletePaymentProofFiles(matterId, paymentId, fileNames = []) {
  const mid = String(matterId || '').trim();
  const pid = String(paymentId || '').trim();
  if (!mid || !pid) return 0;

  const names = (fileNames || []).map((name) => String(name || '').trim()).filter(Boolean);
  let removed = 0;

  if (names.length) {
    for (const name of names) {
      await deleteRecord(paymentProofRecordId(mid, pid, name));
      removed += 1;
    }
    return removed;
  }

  // Fall back to scanning the byPayment index / memory when names are unknown.
  const prefix = `${mid}::${pid}::`;
  for (const id of [...memoryStore.keys()]) {
    if (String(id).startsWith(prefix)) {
      await deleteRecord(id);
      removed += 1;
    }
  }

  try {
    const db = await openDb();
    if (!db) return removed;
    const rows = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const index = tx.objectStore(STORE_NAME).index('byPayment');
      const req = index.getAll([mid, pid]);
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    for (const row of rows) {
      if (!row?.id) continue;
      await deleteRecord(row.id);
      removed += 1;
    }
  } catch {
    // Best-effort cleanup.
  }

  return removed;
}

export function createLocalFileObjectUrl(file) {
  if (!file) return null;
  try {
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
}

/** @internal test helper */
export function __resetPaymentProofMemory() {
  memoryStore.clear();
}
